// src/App.jsx
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import Home from "./pages/Home";
import Login from "./pages/Login";
import LoginRoleFirst from "./pages/LoginRoleFirst"; // ✅ NEW: Improved role-first login
import StudentLogin from "./pages/StudentLogin";
import StudentRegister from "./pages/StudentRegister";
import ChangePassword from "./pages/ChangePassword";
import TeacherRegister from "./pages/TeacherRegister";
import VerifyEmail from "./pages/VerifyEmail";
// PR-PERF-2: Lazy load dashboards to reduce main bundle
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboardModern"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboardModern"));
const MasterAdminDashboard = lazy(() => import("./pages/MasterAdminDashboard"));
import AdminPanel from "./pages/AdminPanel";
import FlaggedAccountsAdmin from "./pages/FlaggedAccountsAdmin";
// QuizManager removed - using AI Builder only
// NativeQuizPlayer - ARCHIVED to src/legacy/pages/ (Practice Mode disabled)
// ExamPlayer/ExamPlayerModern - ARCHIVED to src/legacy/pages/ (Practice Mode disabled)
// ExamDemo - ARCHIVED to src/legacy/pages/ (depends on legacy ExamPlayer)
import TestDemo from "./pages/TestDemo";
// StudentManagerSimple moved to lazy load above
import StudentManagerClean from "./pages/StudentManagerClean";
import FirebaseTest from "./pages/FirebaseTest";
import PDFTest from "./pages/PDFTest";
import PendingApproval from "./pages/PendingApproval";
import AdminApproval from "./pages/AdminApproval";
import MigrationUtility from "./pages/MigrationUtility";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
// Billing moved to lazy load above
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Security from "./pages/Security";
import Blog from "./pages/Blog";
import Help from "./pages/Help";
import Status from "./pages/Status";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import FerpaCompliance from "./pages/FerpaCompliance";
import ApiDocs from "./pages/ApiDocs";
import GetStarted from "./pages/GetStarted";
import Testimonials from "./pages/Testimonials";
import SubscriptionRequired from "./pages/SubscriptionRequired";
// PR-PERF-2: Lazy load AI Builder and heavy tools
const AIExamBuilderBalanced = lazy(() => import("./pages/AIExamBuilderBalanced"));
const ExamPlayerUnified = lazy(() => import("./exam-unified/pages/ExamPlayerUnified"));
const EnhancedAssignmentManager = lazy(() => import("./pages/EnhancedAssignmentManager"));
const QuizLibraryV2 = lazy(() => import("./pages/QuizLibraryV2"));
const TeacherPublicExams = lazy(() => import("./pages/TeacherPublicExams"));
const ClassManager = lazy(() => import("./pages/ClassManager"));
const StudentManagerSimple = lazy(() => import("./pages/StudentManagerSimple"));
const Billing = lazy(() => import("./pages/Billing"));
// Keep QuizLibrary and AssignmentManager direct (legacy backups, rarely used)
import QuizLibrary from "./pages/QuizLibrary";
import AssignmentManager from "./pages/AssignmentManagerSimple";
// TrialVerified and CardVerification removed: $1 verification deprecated
import Lessons from "./pages/Lessons";
import LessonViewer from "./pages/LessonViewer";
// ✅ LAZY LOADED: Heavy monitoring components (saves ~200KB initial bundle)
const TeacherMonitor = lazy(() => import("./pages/TeacherMonitor"));
const TeacherViolations = lazy(() => import("./pages/TeacherViolations"));
const TeacherLiveMonitor = lazy(() => import("./pages/TeacherLiveMonitor"));
import TeacherActivity from "./pages/TeacherActivity";
import TeacherCalendar from "./pages/TeacherCalendar";
// ClassManager moved to lazy load above
import NotFound from "./pages/NotFound";
import { ToastProvider } from "./components/common/Toast";
import ProtectedRoute from "./components/ProtectedRoute";
// PR-PERF-2: Professional loading fallback for lazy routes
import PageLoadingFallback from "./components/PageLoadingFallback";
import { ROLES } from "./constants/roles";
import RoleDashboardRedirect from "./components/RoleDashboardRedirect";
import TeacherLayout from "./components/TeacherLayout";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import DebugBar from "./components/DebugBar";
import DevPanel from "./components/DevPanel";
import ScrollToTop from "./components/ScrollToTop";
import { useLocation } from "react-router-dom";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ThemeProvider } from "./contexts/ThemeContext";

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <SubscriptionProvider>
          <Router>
            <AppContent />
          </Router>
        </SubscriptionProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const location = useLocation();
  
  // Dashboard pages that should use minimal footer (or no footer)
  const isDashboardPage = [
    '/dashboard',
    '/teacher/ai-builder',
    '/teacher/assignments',
    '/teacher/monitor',
    '/student-manager',
    '/student-dashboard',
    '/master-admin'
  ].some(path => location.pathname.startsWith(path));

  // Full-screen pages (no navbar, no footer)
  const isFullScreenPage = [
    '/teacher/monitor'
  ].some(path => location.pathname.includes(path));

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      {!isFullScreenPage && <Navbar />}
      {import.meta.env.DEV && <DebugBar />}
      <main className="min-h-screen">
        {/* PR-PERF-2: App-level Suspense for all lazy-loaded routes */}
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginRoleFirst />} />
            {/* Redirect student-login to unified login flow */}
            <Route path="/student-login" element={<Navigate to="/login" replace />} />
            <Route path="/change-password" element={<ChangePassword />} />
            {/* Student registration removed - only teachers can create student accounts */}
            <Route path="/teacher-register" element={<TeacherRegister />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/admin-panel" element={<AdminPanel />} />
            <Route path="/firebase-test" element={<FirebaseTest />} />
            <Route path="/pdf-test" element={<PDFTest />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            {/* ExamDemo archived - depends on legacy ExamPlayer */}
            {/* <Route path="/exam-demo" element={<ExamDemo />} /> */}
            <Route path="/test-demo" element={<TestDemo />} />
            
            {/* ✅ PUBLIC EXAM / SECURE LINK MODE - No login required */}
            {/* UNIFIED PLAYER ENABLED - Using ExamPlayerUnified for public exams */}
            {/* Rollback: Change back to <PublicExamJoin /> if issues occur */}
            <Route path="/exam/:examCode" element={<ExamPlayerUnified />} />
            
            {/* Professional Pages */}
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/security" element={<Security />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/help" element={<Help />} />
            <Route path="/status" element={<Status />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/ferpa" element={<FerpaCompliance />} />
            <Route path="/api-docs" element={<ApiDocs />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/testimonials" element={<Testimonials />} />

            <Route
              path="/billing"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription-required"
              element={
                <ProtectedRoute>
                  <SubscriptionRequired />
                </ProtectedRoute>
              }
            />

            {/* /trial-verified and /verify-card routes removed: $1 verification deprecated */}

            <Route
              path="/my-dashboard"
              element={
                <ProtectedRoute>
                  <RoleDashboardRedirect />
                </ProtectedRoute>
              }
            />

            {/* Master Admin */}
            <Route
              path="/master-admin"
              element={
                <ProtectedRoute requiredRole={ROLES.MASTER}>
                  <MasterAdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin Approval */}
            <Route
              path="/admin-approval"
              element={
                <ProtectedRoute requiredRole={ROLES.MASTER}>
                  <AdminApproval />
                </ProtectedRoute>
              }
            />

            {/* Flagged Accounts Admin */}
            <Route
              path="/admin/flagged-accounts"
              element={
                <ProtectedRoute requiredRole={ROLES.MASTER}>
                  <FlaggedAccountsAdmin />
                </ProtectedRoute>
              }
            />

            {/* Migration Utility */}
            <Route
              path="/migration-utility"
              element={
                <ProtectedRoute requiredRole="masterAdmin">
                  <MigrationUtility />
                </ProtectedRoute>
              }
            />

            {/* Keep one canonical teacher home */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <TeacherDashboard />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Redirect incorrect /teacher/dashboard to correct /dashboard */}
            <Route path="/teacher/dashboard" element={<Navigate to="/dashboard" replace />} />

            {/* Quiz Manager removed - using AI Builder only */}

            {/* Student Manager for Teachers */}
            <Route
              path="/student-manager"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <StudentManagerSimple />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />


            {/* Quiz List removed - using AI Builder only */}
            {/* Quiz Builder removed - using AI Builder only */}

            {/* AI Exam Builder for Teachers */}
            <Route
              path="/teacher/ai-builder"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <AIExamBuilderBalanced />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Legacy route redirects for old bookmarks */}
            <Route path="/teacher/ai-builder-simple" element={<Navigate to="/teacher/ai-builder" replace />} />
            <Route path="/teacher/ai-builder-balanced" element={<Navigate to="/teacher/ai-builder" replace />} />

            {/* Quiz Library for Teachers - Modern Version */}
            <Route
              path="/teacher/quiz-library"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <QuizLibraryV2 />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Legacy Quiz Library (backup) */}
            <Route
              path="/teacher/quiz-library-legacy"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <QuizLibrary />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Public Exams Management */}
            <Route
              path="/teacher/public-exams"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <TeacherPublicExams />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Lessons for Teachers */}
            <Route
              path="/lessons"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <Lessons />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/lessons/:lessonId"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <LessonViewer />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Assignment Manager for Teachers */}
            <Route
              path="/teacher/assignments"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <EnhancedAssignmentManager />
                </ProtectedRoute>
              }
            />

            {/* Individual Assignment View/Details */}
            <Route
              path="/teacher/assignments/:assignmentId"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherMonitor />
                </ProtectedRoute>
              }
            />

            {/* Teacher Monitoring for Assignments */}
            <Route
              path="/teacher/monitor/:assignmentId"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherMonitor />
                </ProtectedRoute>
              }
            />

            {/* Teacher Security Violations */}
            <Route
              path="/teacher/violations"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <TeacherViolations />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Teacher Live Monitor */}
            <Route
              path="/teacher/monitor/live"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <TeacherLiveMonitor />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Teacher Activity Feed */}
            <Route
              path="/teacher/activity"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <TeacherActivity />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Teacher Calendar */}
            <Route
              path="/teacher/calendar"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <TeacherCalendar />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Class Manager */}
            <Route
              path="/teacher/classes"
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherLayout>
                    <ClassManager />
                  </TeacherLayout>
                </ProtectedRoute>
              }
            />

            {/* Quiz Results removed - using AI Builder only */}

            {/* AssignStudents removed - using AI Builder direct assignment */}

            {/* Student Dashboard */}
            <Route
              path="/student-dashboard"
              element={
                <ProtectedRoute requiredRole="student">
                  <StudentDashboard />
                </ProtectedRoute>
              }
            />


            {/* Redirect legacy quiz paths to canonical */}
            <Route path="/take-quiz/:quizId" element={<Navigate to="/student-dashboard" replace />} />
            <Route path="/native-quiz/:quizId" element={<Navigate to="/student-dashboard" replace />} />

            {/* Canonical route for assignment-based quiz taking */}
            <Route
              path="/student/take/:assignmentId"
              element={
                <ProtectedRoute requiredRole="student">
                  <ExamPlayerUnified mode="assignment" />
                </ProtectedRoute>
              }
            />

            {/* Practice Mode disabled for MVP - depends on legacy ExamPlayer */}
            {/* Re-enable only after rebuilding on ExamPlayerUnified */}
            {/* <Route path="/student/practice/:assignmentId" element={<Navigate to="/student-dashboard" replace />} /> */}

            {/* 404 - Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {/* Only show full footer on non-dashboard pages */}
      {!isDashboardPage && <Footer />}

      {/* Dev Panel for testing - shows only in development or for tester accounts */}
      <DevPanel />
    </div>
  );
}

export default App;

